import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money, openImpersonation } from "@/lib/api";
import { can, canImpersonate } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Storefront, Plus, ArrowLeft, ArrowRight, SlidersHorizontal, SignIn, Prohibit, Play, Trash,
} from "@phosphor-icons/react";
import CreateWizardSteps from "@/components/CreateWizardSteps";
import AdjustBalanceModal from "@/components/AdjustBalanceModal";

const emptyResellerForm = {
  name: "", company: "", email: "", password: "", contact_phone: "",
  call_limit_pricing: "standard", call_limit_price: "",
  white_label_pricing: "standard", white_label_price: "",
  opensips_server_ids: [], media_server_ids: [], ai_engine_ids: [],
  ai_model_id: "__none__", tts_id: "", stt_id: "",
};

const RESELLER_STEPS = ["Details", "Engines", "AI Config", "Pricing"];

export default function Resellers() {
  const { user } = useAuth();
  const canAdd = can(user, "resellers", "add");
  const canEdit = can(user, "resellers", "edit");
  const canDelete = can(user, "resellers", "delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [assignees, setAssignees] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyResellerForm);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [stdPrices, setStdPrices] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState(null);
  const [wlTarget, setWlTarget] = useState(null);
  const [wlBusy, setWlBusy] = useState(false);
  const [createOps, setCreateOps] = useState([]);
  const [createMed, setCreateMed] = useState([]);
  const [createEng, setCreateEng] = useState([]);
  const [catalogs, setCatalogs] = useState({ models: [], tts: [], stt: [] });

  const wlLabel = (status) => {
    if (status === "active") return "Enabled";
    if (status === "lapsed") return "Lapsed";
    return "Disabled";
  };

  useEffect(() => {
    if (!open) { setStep(0); return; }
    api.get("/billing/pricing").then(({ data }) => setStdPrices(data || null)).catch(() => setStdPrices(null));
    Promise.all([
      api.get("/servers/opensips"),
      api.get("/servers/media"),
      api.get("/ai-engines"),
      api.get("/ai-bots/models"),
      api.get("/ai-bots/tts"),
      api.get("/ai-bots/stt"),
    ])
      .then(([o, m, e, models, tts, stt]) => {
        const ops = o.data || [];
        const med = m.data || [];
        const eng = e.data || [];
        setCreateOps(ops);
        setCreateMed(med);
        setCreateEng(eng);
        const cat = { models: models.data || [], tts: tts.data || [], stt: stt.data || [] };
        setCatalogs(cat);
        const defModel = cat.models.find((r) => r.is_default && r.status === "active");
        const defTts = cat.tts.find((r) => r.is_default && r.status === "active");
        const defStt = cat.stt.find((r) => r.is_default && r.status === "active");
        setForm((f) => ({
          ...f,
          opensips_server_ids: ops.filter((s) => s.is_default).map((s) => s.id),
          media_server_ids: med.filter((s) => s.is_default).map((s) => s.id),
          ai_engine_ids: eng.filter((s) => s.is_default && s.enabled !== false && s.status !== "DISABLED").map((s) => s.id),
          ai_model_id: f.ai_model_id !== "__none__" ? f.ai_model_id : (defModel?.id || "__none__"),
          tts_id: f.tts_id || defTts?.id || "",
          stt_id: f.stt_id || defStt?.id || "",
        }));
      })
      .catch(() => {});
  }, [open]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/resellers/${delTarget.id}`);
      toast.success("Reseller deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const doDisableWhiteLabel = async () => {
    if (!wlTarget) return;
    setWlBusy(true);
    try {
      await api.post(`/resellers/${wlTarget.id}/white-label/disable`);
      toast.success("White label disabled");
      setWlTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setWlBusy(false);
    }
  };

  const doEnableWhiteLabel = async () => {
    if (!wlTarget) return;
    setWlBusy(true);
    try {
      await api.post(`/resellers/${wlTarget.id}/white-label/enable`);
      toast.success("White label re-enabled");
      setWlTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setWlBusy(false);
    }
  };

  const wlConfigured = !!(wlTarget?.white_label_domain);

  const isFullAdmin = user?.role === "admin";
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isFullAdmin && assignedFilter && assignedFilter !== "all") params.assigned_to = assignedFilter;
      const { data } = await api.get("/resellers", { params });
      setRows(data || []);
      if (isFullAdmin) {
        try {
          const { data: a } = await api.get("/admin-users/assignees");
          setAssignees(a || []);
        } catch {
          setAssignees([]);
        }
      } else {
        setAssignees([]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [assignedFilter, isFullAdmin]);
  useEffect(() => { load(); }, [load]);

  const canNextDetails = form.name.trim() && form.company.trim() && form.email.trim() && form.password.length >= 8;

  const nextStep = () => {
    if (step === 0 && !canNextDetails) {
      toast.error(form.password && form.password.length < 8
        ? "Password must be at least 8 characters"
        : "Fill in contact name, company, email, and password.");
      return;
    }
    if (step === 2 && (!form.tts_id || !form.stt_id)) {
      toast.error("Select TTS and STT for this reseller. New customers inherit this AI configuration.");
      return;
    }
    setStep((s) => Math.min(RESELLER_STEPS.length - 1, s + 1));
  };

  const catalogOptions = (list, selectedId) =>
    (list || []).filter((r) => r.status === "active" || r.id === selectedId);

  const create = async () => {
    if (!form.tts_id || !form.stt_id) {
      toast.error("Select TTS and STT for this reseller.");
      setStep(2);
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name, company: form.company, email: form.email,
        password: form.password, contact_phone: form.contact_phone,
        opensips_server_ids: form.opensips_server_ids,
        media_server_ids: form.media_server_ids,
        ai_engine_ids: form.ai_engine_ids,
        ai_model_id: form.ai_model_id === "__none__" ? null : form.ai_model_id,
        tts_id: form.tts_id,
        stt_id: form.stt_id,
      };
      body.call_limit_pricing = form.call_limit_pricing;
      if (form.call_limit_pricing === "custom")
        body.call_limit_price_minor = Math.max(0, Math.round(parseFloat(form.call_limit_price || 0) * 100));
      body.white_label_pricing = form.white_label_pricing;
      if (form.white_label_pricing === "custom")
        body.white_label_price_minor = Math.max(0, Math.round(parseFloat(form.white_label_price || 0) * 100));
      await api.post("/resellers", body);
      toast.success("Reseller created");
      setOpen(false); setForm(emptyResellerForm); setStep(0); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "active" ? "suspended" : "active";
    try { await api.patch(`/resellers/${r.id}/status`, { status: next }); toast.success(`Reseller ${next}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Resellers" subtitle="Platform resellers — balance, pricing and their customers">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep(0); setForm(emptyResellerForm); } }}>
          {canAdd && (
          <DialogTrigger asChild>
            <Button data-testid="create-reseller-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Reseller</Button>
          </DialogTrigger>
          )}
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="create-reseller-dialog">
            <DialogHeader>
              <DialogTitle>Create reseller</DialogTitle>
              <DialogDescription>Step {step + 1} of {RESELLER_STEPS.length}: {RESELLER_STEPS[step]}</DialogDescription>
            </DialogHeader>
            <CreateWizardSteps steps={RESELLER_STEPS} current={step} onSelect={setStep} testid="reseller-wizard-steps" />

            {step === 0 && (
              <div className="space-y-4 py-1" data-testid="reseller-wizard-details">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Contact name</Label><Input data-testid="reseller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Company</Label><Input data-testid="reseller-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label><Input data-testid="reseller-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>Password</Label><Input data-testid="reseller-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" /></div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 py-1" data-testid="reseller-wizard-engines">
                <p className="text-sm text-muted-foreground">Servers this reseller can assign to its customers.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>OpenSIPS</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                      {createOps.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={form.opensips_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, opensips_server_ids: v ? [...f.opensips_server_ids, s.id] : f.opensips_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                      {createOps.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>FreeSWITCH (Media)</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                      {createMed.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={form.media_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, media_server_ids: v ? [...f.media_server_ids, s.id] : f.media_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                      {createMed.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>AI Engine servers</Label>
                  <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-40 overflow-y-auto">
                    {createEng.filter((s) => s.enabled !== false && s.status !== "DISABLED").map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={form.ai_engine_ids.includes(s.id)}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, ai_engine_ids: v ? [...f.ai_engine_ids, s.id] : f.ai_engine_ids.filter((x) => x !== s.id) }))} />
                        {s.name} <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 py-1" data-testid="reseller-wizard-ai">
                <p className="text-sm text-muted-foreground">
                  AI configuration assigned to this reseller. Customers they create inherit these automatically.
                </p>
                <div className="space-y-2">
                  <Label>AI Model <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                  <Select value={form.ai_model_id} onValueChange={(v) => setForm({ ...form, ai_model_id: v })}>
                    <SelectTrigger data-testid="reseller-create-ai-model"><SelectValue placeholder="No model" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No model</SelectItem>
                      {catalogOptions(catalogs.models, form.ai_model_id).map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>TTS</Label>
                    <Select value={form.tts_id || undefined} onValueChange={(v) => setForm({ ...form, tts_id: v })}>
                      <SelectTrigger data-testid="reseller-create-ai-tts"><SelectValue placeholder="Select TTS" /></SelectTrigger>
                      <SelectContent>
                        {catalogOptions(catalogs.tts, form.tts_id).map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>STT</Label>
                    <Select value={form.stt_id || undefined} onValueChange={(v) => setForm({ ...form, stt_id: v })}>
                      <SelectTrigger data-testid="reseller-create-ai-stt"><SelectValue placeholder="Select STT" /></SelectTrigger>
                      <SelectContent>
                        {catalogOptions(catalogs.stt, form.stt_id).map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 py-1" data-testid="reseller-wizard-pricing">
                <div className="space-y-2">
                  <Label>Call-limit pricing</Label>
                  <Select value={form.call_limit_pricing} onValueChange={(v) => setForm({ ...form, call_limit_pricing: v })}>
                    <SelectTrigger data-testid="reseller-pricing-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (admin reseller price)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.call_limit_pricing === "custom" && (
                    <Input data-testid="reseller-pricing-custom" type="number" min="0" step="0.01" value={form.call_limit_price} onChange={(e) => setForm({ ...form, call_limit_price: e.target.value })} placeholder="Custom price per channel (USD)" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>White label pricing</Label>
                  <Select value={form.white_label_pricing} onValueChange={(v) => setForm({ ...form, white_label_pricing: v })}>
                    <SelectTrigger data-testid="reseller-white-label-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (admin white label price)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.white_label_pricing === "custom" && (
                    <Input data-testid="reseller-white-label-custom" type="number" min="0" step="0.01" value={form.white_label_price} onChange={(e) => setForm({ ...form, white_label_price: e.target.value })} placeholder="Custom white label fee (USD / month)" />
                  )}
                </div>
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2" data-testid="reseller-create-assigned-pricing">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Prices that will be assigned</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Call limit / channel</p>
                      <p className="font-semibold tabular-nums">
                        {form.call_limit_pricing === "custom" && form.call_limit_price !== ""
                          ? money(Math.round(parseFloat(form.call_limit_price || 0) * 100))
                          : (stdPrices?.reseller_price_minor != null ? money(stdPrices.reseller_price_minor) : "Not set")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">White label / month</p>
                      <p className="font-semibold tabular-nums">
                        {form.white_label_pricing === "custom" && form.white_label_price !== ""
                          ? money(Math.round(parseFloat(form.white_label_price || 0) * 100))
                          : (stdPrices?.reseller_white_label_price_minor != null ? money(stdPrices.reseller_white_label_price_minor) : "Not set")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">AI Model / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.reseller_ai_model_price_minor != null ? money(stdPrices.reseller_ai_model_price_minor) : "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">TTS / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.reseller_tts_price_minor != null ? money(stdPrices.reseller_tts_price_minor) : "Not set"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">STT / min</p>
                      <p className="font-semibold tabular-nums">
                        {stdPrices?.reseller_stt_price_minor != null ? money(stdPrices.reseller_stt_price_minor) : "Not set"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              {step > 0 && (
                <Button type="button" variant="outline" className="rounded-lg" onClick={() => setStep((s) => s - 1)} data-testid="reseller-wizard-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              {step < RESELLER_STEPS.length - 1 ? (
                <Button type="button" className="rounded-lg" onClick={nextStep} data-testid="reseller-wizard-next">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={create} disabled={saving} data-testid="submit-reseller-btn" className="rounded-lg">
                  {saving ? "Creating…" : "Create reseller"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isFullAdmin && (
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="reseller-filters">
        <div className="space-y-1.5 min-w-[200px]">
          <Label className="text-xs text-muted-foreground">Assigned to</Label>
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger data-testid="filter-assigned-to" className="h-9 rounded-lg"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {assignees.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name || a.email}{a.role === "staff" ? " (staff)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      )}

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="resellers-empty" icon={Storefront} title="No resellers yet"
            description="Create your first reseller. They hold a platform wallet and manage their own customers." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="resellers-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Company</TableHead><TableHead>Status</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>White label</TableHead>
                <TableHead className="text-center">Customers</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Call-limit</TableHead>
                <TableHead>Signup date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const wlStatus = r.white_label_status || "inactive";
                  const wlEnabled = wlStatus === "active" || wlStatus === "lapsed";
                  return (
                  <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`reseller-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground" data-testid={`reseller-assigned-${r.id}`}>
                      {r.assigned_to_name || "—"}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="inline-flex"
                        onClick={() => setWlTarget(r)}
                        data-testid={`reseller-wl-${r.id}`}
                        title={wlEnabled ? "Manage white label" : "White label status"}
                      >
                        <StatusPill status={wlLabel(wlStatus).toLowerCase()} />
                      </button>
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{r.customer_count}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <button
                        type="button"
                        className="font-semibold text-primary hover:underline underline-offset-2"
                        onClick={() => setBalanceTarget(r)}
                        data-testid={`reseller-balance-${r.id}`}
                        title="Adjust balance"
                      >
                        {money(r.cash_balance_minor || 0)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`reseller-calllimit-${r.id}`}>{r.concurrent_call_limit != null ? r.concurrent_call_limit : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`reseller-signup-${r.id}`}>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => setManage(r)}
                          data-testid={`manage-reseller-${r.id}`} title="Manage" aria-label="Manage">
                          <SlidersHorizontal className="w-4 h-4" />
                        </Button>
                        )}
                        {canImpersonate(user) && (
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" data-testid={`login-as-reseller-${r.id}`}
                          title="Login as" aria-label="Login as"
                          onClick={async () => { try { await openImpersonation(r.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
                          <SignIn className="w-4 h-4" />
                        </Button>
                        )}
                        {canEdit && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleStatus(r)}
                          data-testid={`toggle-reseller-${r.id}`}
                          title={r.status === "active" ? "Suspend" : "Activate"}
                          aria-label={r.status === "active" ? "Suspend" : "Activate"}>
                          {r.status === "active" ? <Prohibit className="w-4 h-4" /> : <Play className="w-4 h-4" weight="fill" />}
                        </Button>
                        )}
                        {canDelete && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDelTarget(r)} data-testid={`delete-reseller-${r.id}`} title="Delete" aria-label="Delete">
                          <Trash className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

      <ManageReseller reseller={manage} onClose={() => setManage(null)} onSaved={load} />

      <AdjustBalanceModal
        account={balanceTarget}
        open={!!balanceTarget}
        onClose={() => setBalanceTarget(null)}
        onSaved={() => load()}
      />

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent data-testid="delete-reseller-dialog">
          <DialogHeader><DialogTitle>Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription>
              This deactivates the reseller and its login. Any customers owned by this reseller must be deleted or reassigned first. This cannot be undone from the UI.
            </DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDelTarget(null)} data-testid="cancel-delete-reseller">Cancel</Button>
            <Button className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-reseller">
              {deleting ? "Deleting…" : "Delete reseller"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!wlTarget} onOpenChange={(v) => !v && !wlBusy && setWlTarget(null)}>
        <DialogContent data-testid="disable-white-label-dialog">
          <DialogHeader>
            <DialogTitle>White label — {wlTarget?.name}</DialogTitle>
            <DialogDescription>
              {(wlTarget?.white_label_status === "active" || wlTarget?.white_label_status === "lapsed")
                ? "Disable white label to stop branded public access on this reseller’s domain."
                : wlConfigured
                  ? "White label is disabled. Re-enable to restore branded access on the configured domain."
                  : "White label is not configured for this reseller yet."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm space-y-1">
            <p>
              <span className="text-muted-foreground">Status:</span>{" "}
              <span className="font-medium">{wlLabel(wlTarget?.white_label_status)}</span>
            </p>
            {wlTarget?.white_label_domain && (
              <p>
                <span className="text-muted-foreground">Domain:</span>{" "}
                <span className="font-medium">{wlTarget.white_label_domain}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setWlTarget(null)} disabled={wlBusy} data-testid="cancel-disable-wl">
              Close
            </Button>
            {(wlTarget?.white_label_status === "active" || wlTarget?.white_label_status === "lapsed") && (
              <Button
                className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={doDisableWhiteLabel}
                disabled={wlBusy}
                data-testid="confirm-disable-wl"
              >
                {wlBusy ? "Disabling…" : "Disable white label"}
              </Button>
            )}
            {(wlTarget?.white_label_status || "inactive") === "inactive" && wlConfigured && (
              <Button
                className="rounded-lg"
                onClick={doEnableWhiteLabel}
                disabled={wlBusy}
                data-testid="confirm-enable-wl"
              >
                {wlBusy ? "Re-enabling…" : "Re-enable"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MANAGE_RESELLER_STEPS = ["Details", "Engines", "AI Config", "Pricing"];

function ManageReseller({ reseller, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [limit, setLimit] = useState("");
  const [pricingMode, setPricingMode] = useState("standard");
  const [customPrice, setCustomPrice] = useState("");
  const [whiteLabelMode, setWhiteLabelMode] = useState("standard");
  const [whiteLabelCustomPrice, setWhiteLabelCustomPrice] = useState("");
  const [usagePrices, setUsagePrices] = useState({ ai: "", tts: "", stt: "" });
  const [busy, setBusy] = useState(false);
  const dollars = (m) => (m != null ? (m / 100).toFixed(2) : "");
  const [allOps, setAllOps] = useState([]);
  const [allMed, setAllMed] = useState([]);
  const [allEng, setAllEng] = useState([]);
  const [selOps, setSelOps] = useState([]);
  const [selMed, setSelMed] = useState([]);
  const [selEng, setSelEng] = useState([]);
  const [catalogs, setCatalogs] = useState({ models: [], tts: [], stt: [] });
  const [aiForm, setAiForm] = useState({ ai_model_id: "__none__", tts_id: "", stt_id: "" });
  const [stdPrices, setStdPrices] = useState(null);

  useEffect(() => {
    if (!reseller) {
      setDetail(null);
      setStep(0);
      return;
    }
    setStep(0);
    setNewPassword("");
    Promise.all([
      api.get("/billing/pricing"),
      api.get(`/resellers/${reseller.id}`),
    ]).then(([prices, r]) => {
      const std = prices.data || null;
      setStdPrices(std);
      setDetail(r.data);
      setCompany(r.data.name || "");
      setContactName(r.data.contact_name || "");
      setEmail(r.data.login_email || r.data.contact_email || "");
      setSelOps(r.data.opensips_server_ids || []);
      setSelMed(r.data.media_server_ids || []);
      setSelEng(r.data.ai_engine_ids || []);
      setLimit((r.data.concurrent_call_limit ?? "") === "" ? "" : String(r.data.concurrent_call_limit ?? ""));
      setPricingMode(r.data.call_limit_pricing_mode || "standard");
      setCustomPrice(r.data.call_limit_price_minor != null ? (r.data.call_limit_price_minor / 100).toFixed(2) : "");
      const wlMode = r.data.white_label_pricing_mode || "standard";
      setWhiteLabelMode(wlMode);
      const wlAssigned = r.data.white_label_price_minor;
      const wlStd = std?.reseller_white_label_price_minor;
      setWhiteLabelCustomPrice(
        wlMode === "standard" && wlStd != null
          ? (wlStd / 100).toFixed(2)
          : wlAssigned != null
            ? (wlAssigned / 100).toFixed(2)
            : wlStd != null
              ? (wlStd / 100).toFixed(2)
              : "",
      );
      setUsagePrices({
        ai: dollars(r.data.ai_model_price_minor),
        tts: dollars(r.data.tts_price_minor),
        stt: dollars(r.data.stt_price_minor),
      });
    }).catch(() => setDetail(reseller));
    Promise.all([
      api.get("/servers/opensips"),
      api.get("/servers/media"),
      api.get("/ai-engines"),
      api.get("/ai-bots/models"),
      api.get("/ai-bots/tts"),
      api.get("/ai-bots/stt"),
      api.get(`/ai-bots/resellers/${reseller.id}`),
    ])
      .then(([o, m, e, models, tts, stt, assigned]) => {
        setAllOps(o.data || []);
        setAllMed(m.data || []);
        setAllEng(e.data || []);
        setCatalogs({ models: models.data || [], tts: tts.data || [], stt: stt.data || [] });
        const a = assigned.data || {};
        setAiForm({
          ai_model_id: a.ai_model_id || "__none__",
          tts_id: a.tts_id || "",
          stt_id: a.stt_id || "",
        });
      })
      .catch(() => {});
  }, [reseller]);

  if (!reseller) return null;

  const toggle = (setter) => (id, v) => setter((p) => (v ? [...p, id] : p.filter((x) => x !== id)));
  const toMinor = (v) => (v === "" || v == null || Number.isNaN(parseFloat(v))
    ? null
    : Math.max(0, Math.round(parseFloat(v) * 100)));
  const optionRows = (list, selectedId) =>
    (list || []).filter((r) => r.status === "active" || r.id === selectedId);

  const canNextDetails = company.trim() && contactName.trim() && email.trim()
    && (!newPassword || newPassword.length >= 8);

  const nextStep = () => {
    if (step === 0 && !canNextDetails) {
      toast.error(newPassword && newPassword.length < 8
        ? "Password must be at least 8 characters"
        : "Company, contact name, and email are required.");
      return;
    }
    if (step === 2 && (!aiForm.tts_id || !aiForm.stt_id)) {
      toast.error("Select TTS and STT for this reseller.");
      return;
    }
    setStep((s) => Math.min(MANAGE_RESELLER_STEPS.length - 1, s + 1));
  };

  const saveAll = async () => {
    if (newPassword && newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      setStep(0);
      return;
    }
    if (!aiForm.tts_id || !aiForm.stt_id) {
      toast.error("TTS and STT are required.");
      setStep(2);
      return;
    }
    setBusy(true);
    try {
      const body = {
        company: company.trim(),
        name: contactName.trim(),
        email: email.trim(),
        concurrent_call_limit: limit === "" ? 0 : Math.max(0, parseInt(limit, 10) || 0),
        call_limit_pricing: pricingMode,
        ...(pricingMode === "custom"
          ? { call_limit_price_minor: Math.max(0, Math.round(parseFloat(customPrice || 0) * 100)) }
          : {}),
        white_label_pricing: whiteLabelMode,
        ...(whiteLabelMode === "custom"
          ? { white_label_price_minor: Math.max(0, Math.round(parseFloat(whiteLabelCustomPrice || 0) * 100)) }
          : {}),
        ai_model_price_minor: toMinor(usagePrices.ai),
        tts_price_minor: toMinor(usagePrices.tts),
        stt_price_minor: toMinor(usagePrices.stt),
      };
      if (newPassword) body.password = newPassword;
      await api.patch(`/resellers/${reseller.id}`, body);
      await api.put(`/resellers/${reseller.id}/servers`, {
        opensips_server_ids: selOps,
        media_server_ids: selMed,
        ai_engine_ids: selEng,
      });
      await api.put(`/ai-bots/resellers/${reseller.id}`, {
        ai_model_id: aiForm.ai_model_id === "__none__" ? null : aiForm.ai_model_id,
        tts_id: aiForm.tts_id,
        stt_id: aiForm.stt_id,
      });
      toast.success("Reseller updated");
      setNewPassword("");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  /** Save from any step — admin can finish after editing Details/Engines/etc. */
  const finishEarly = () => {
    if (!canNextDetails) {
      toast.error(newPassword && newPassword.length < 8
        ? "Password must be at least 8 characters"
        : "Company, contact name, and email are required.");
      setStep(0);
      return;
    }
    saveAll();
  };

  const last = step === MANAGE_RESELLER_STEPS.length - 1;

  return (
    <Dialog open={!!reseller} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="manage-reseller-dialog">
        <DialogHeader>
          <DialogTitle>Edit reseller · {reseller.name}</DialogTitle>
          <DialogDescription>
            Step {step + 1} of {MANAGE_RESELLER_STEPS.length}: {MANAGE_RESELLER_STEPS[step]}
          </DialogDescription>
        </DialogHeader>

        <CreateWizardSteps steps={MANAGE_RESELLER_STEPS} current={step} onSelect={setStep} testid="manage-reseller-wizard-steps" />

        {step === 0 && (
          <div className="space-y-4 py-1" data-testid="manage-reseller-wizard-details">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Company name</Label>
                <Input data-testid="reseller-edit-company" value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Contact name</Label>
                <Input data-testid="reseller-edit-name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="reseller-edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>
                  New password{" "}
                  <span className="text-muted-foreground text-xs font-normal">(leave blank to keep)</span>
                </Label>
                <Input
                  data-testid="reseller-edit-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>
            <div>
              <Label>Customers ({detail?.customers?.length ?? 0})</Label>
              <div className="mt-2 rounded-md border divide-y max-h-36 overflow-y-auto" data-testid="reseller-customers-list">
                {(detail?.customers || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{c.name}</span>
                    <StatusPill status={c.status} />
                  </div>
                ))}
                {(!detail?.customers || detail.customers.length === 0) && (
                  <p className="px-3 py-3 text-sm text-muted-foreground">No customers yet.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 py-1" data-testid="manage-reseller-wizard-engines">
            <p className="text-sm text-muted-foreground">
              The reseller can only pass these servers to their own customers.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto" data-testid="reseller-opensips-list">
                <p className="text-xs font-medium">OpenSIPS</p>
                {allOps.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`reseller-opensips-${s.id}`}
                      checked={selOps.includes(s.id)}
                      onCheckedChange={(v) => toggle(setSelOps)(s.id, v)}
                    />
                    {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                  </label>
                ))}
                {allOps.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
              </div>
              <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto" data-testid="reseller-media-list">
                <p className="text-xs font-medium">FreeSWITCH (Media)</p>
                {allMed.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`reseller-media-${s.id}`}
                      checked={selMed.includes(s.id)}
                      onCheckedChange={(v) => toggle(setSelMed)(s.id, v)}
                    />
                    {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                  </label>
                ))}
                {allMed.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
              </div>
            </div>
            <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto" data-testid="reseller-aiengine-list">
              <p className="text-xs font-medium">AI Engine servers</p>
              {allEng.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    data-testid={`reseller-aiengine-${s.id}`}
                    checked={selEng.includes(s.id)}
                    onCheckedChange={(v) => toggle(setSelEng)(s.id, v)}
                  />
                  {s.name}{" "}
                  <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                </label>
              ))}
              {allEng.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-1" data-testid="manage-reseller-wizard-ai">
            <p className="text-sm text-muted-foreground">
              Assigned to this reseller. When they create a customer, these AI Model / TTS / STT values are copied automatically.
            </p>
            <div className="space-y-1.5">
              <Label>AI Model</Label>
              <Select value={aiForm.ai_model_id} onValueChange={(v) => setAiForm((f) => ({ ...f, ai_model_id: v }))}>
                <SelectTrigger data-testid="reseller-manage-ai-model"><SelectValue placeholder="No model" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No model</SelectItem>
                  {optionRows(catalogs.models, aiForm.ai_model_id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>TTS</Label>
                <Select value={aiForm.tts_id || undefined} onValueChange={(v) => setAiForm((f) => ({ ...f, tts_id: v }))}>
                  <SelectTrigger data-testid="reseller-manage-ai-tts"><SelectValue placeholder="Select TTS" /></SelectTrigger>
                  <SelectContent>
                    {optionRows(catalogs.tts, aiForm.tts_id).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>STT</Label>
                <Select value={aiForm.stt_id || undefined} onValueChange={(v) => setAiForm((f) => ({ ...f, stt_id: v }))}>
                  <SelectTrigger data-testid="reseller-manage-ai-stt"><SelectValue placeholder="Select STT" /></SelectTrigger>
                  <SelectContent>
                    {optionRows(catalogs.stt, aiForm.stt_id).map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.provider} · {r.name}{r.status !== "active" ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-1" data-testid="manage-reseller-wizard-pricing">
            <div className="space-y-2">
              <Label>
                Concurrent call limit{" "}
                <span className="text-muted-foreground text-xs font-normal normal-case">
                  (max across all customers; 0 = no calls
                  {detail?.active_calls != null ? ` · ${detail.active_calls} active now` : ""})
                </span>
              </Label>
              <Input
                data-testid="reseller-manage-concurrent-limit"
                type="number"
                min="0"
                step="1"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="e.g. 500"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Call-limit pricing{" "}
                <span className="text-muted-foreground text-xs font-normal normal-case">
                  (per concurrent channel
                  {detail?.call_limit_price_minor != null
                    ? ` · current $${(detail.call_limit_price_minor / 100).toFixed(2)} (${detail.call_limit_pricing_mode || "—"})`
                    : ""}
                  )
                </span>
              </Label>
              <div className="flex gap-2 items-start">
                <Select value={pricingMode} onValueChange={setPricingMode}>
                  <SelectTrigger data-testid="reseller-manage-pricing-mode" className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (admin reseller price)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {pricingMode === "custom" && (
                  <Input
                    data-testid="reseller-manage-pricing-custom"
                    type="number"
                    min="0"
                    step="0.01"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    placeholder="USD / channel"
                    className="flex-1"
                  />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>
                White label pricing{" "}
                <span className="text-muted-foreground text-xs font-normal normal-case">
                  (per month
                  {detail?.white_label_price_minor != null
                    ? ` · assigned $${(detail.white_label_price_minor / 100).toFixed(2)} (${detail.white_label_pricing_mode || "—"})`
                    : ""}
                  )
                </span>
              </Label>
              <div className="flex gap-2 items-start">
                <Select
                  value={whiteLabelMode}
                  onValueChange={(v) => {
                    setWhiteLabelMode(v);
                    if (v === "standard") {
                      setWhiteLabelCustomPrice(
                        stdPrices?.reseller_white_label_price_minor != null
                          ? (stdPrices.reseller_white_label_price_minor / 100).toFixed(2)
                          : (detail?.white_label_price_minor != null
                            ? (detail.white_label_price_minor / 100).toFixed(2)
                            : ""),
                      );
                    }
                  }}
                >
                  <SelectTrigger data-testid="reseller-manage-white-label-mode" className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (admin white label price)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  data-testid="reseller-manage-white-label-custom"
                  type="number"
                  min="0"
                  step="0.01"
                  value={whiteLabelMode === "standard"
                    ? (stdPrices?.reseller_white_label_price_minor != null
                      ? (stdPrices.reseller_white_label_price_minor / 100).toFixed(2)
                      : whiteLabelCustomPrice)
                    : whiteLabelCustomPrice}
                  onChange={(e) => {
                    setWhiteLabelCustomPrice(e.target.value);
                    if (whiteLabelMode === "standard") setWhiteLabelMode("custom");
                  }}
                  readOnly={whiteLabelMode === "standard"}
                  placeholder={stdPrices?.reseller_white_label_price_minor != null ? "Admin price" : "USD / month"}
                  className={`flex-1 ${whiteLabelMode === "standard" ? "bg-muted/60" : ""}`}
                />
              </div>
              {whiteLabelMode === "standard" && stdPrices?.reseller_white_label_price_minor == null && (
                <p className="text-xs text-amber-600">Admin white label price is not set under Billing → Reseller pricing.</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>
                  AI Model per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="reseller-ai-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.ai}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, ai: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  TTS per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="reseller-tts-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.tts}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, tts: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  STT per min{" "}
                  <span className="text-muted-foreground text-xs font-normal normal-case">(USD)</span>
                </Label>
                <Input
                  data-testid="reseller-stt-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={usagePrices.stt}
                  onChange={(e) => setUsagePrices((p) => ({ ...p, stt: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => setStep((s) => s - 1)}
              data-testid="manage-reseller-wizard-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {!last ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                onClick={finishEarly}
                disabled={busy}
                data-testid="manage-reseller-wizard-finish"
              >
                {busy ? "Saving…" : "Finish"}
              </Button>
              <Button
                type="button"
                className="rounded-lg"
                onClick={nextStep}
                data-testid="manage-reseller-wizard-next"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <Button
              onClick={saveAll}
              disabled={busy}
              data-testid="reseller-save-personal-btn"
              className="rounded-lg"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
